/**
 * Pre-onboarding API client (Phase 1).
 *
 * Thin wrapper around the shared axios instance exported from ./api.
 * Kept in its own module so the pre-onboarding UI can be developed,
 * tested, and mocked independently of the monolithic api.js grab-bag.
 *
 * Routes served by: staff_confidential.pre_onboarding_urls
 *   Mounted at:     /api/v1/confidential/pre-onboarding/
 */

import { api } from './api';

const BASE = '/confidential/pre-onboarding';

// ---------------------------------------------------------------------------
// Candidate Applications (HR-authenticated CRUD)
// ---------------------------------------------------------------------------

/**
 * List candidate applications with optional filters.
 * Supported params: status, track, source, department, q (text search on
 * public_ref / ats_candidate_id).
 */
export const listApplications = (params = {}) =>
  api.get(`${BASE}/applications/`, { params });

/** Retrieve one application by its public_ref (CAND-YYYY-NNNNNN). */
export const getApplication = (publicRef) =>
  api.get(`${BASE}/applications/${encodeURIComponent(publicRef)}/`);

/** HR-initiated create. Set source='hr_manual'. */
export const createApplication = (payload) =>
  api.post(`${BASE}/applications/`, payload);

/**
 * HR "Add Employee" direct hire — creates a complete employee in one call
 * (StaffUser + HRProfile + leave balances + schedule + salary + employment
 * period + audit), reusing the materialisation engine. Returns
 * { hire_type, staff_id, effective_date, ... }. 422 on engine pre-flight
 * failure (e.g. NRIC matches an active employee, start date >30 days past).
 */
export const directHire = (payload) =>
  api.post(`${BASE}/applications/direct-hire/`, payload);

/** Partial update (PATCH) on an application. */
export const updateApplication = (publicRef, payload) =>
  api.patch(`${BASE}/applications/${encodeURIComponent(publicRef)}/`, payload);

/**
 * Transition status. The serializer enforces the allowed lifecycle edges;
 * this helper is just a convenience wrapper.
 */
export const transitionApplication = (publicRef, newStatus, extra = {}) =>
  api.patch(
    `${BASE}/applications/${encodeURIComponent(publicRef)}/`,
    { status: newStatus, ...extra },
  );

/** Soft-cancel an application (sets status=withdrawn). */
export const withdrawApplication = (publicRef, reason = '') =>
  transitionApplication(publicRef, STATUS_WITHDRAWN, reason ? { hr_notes: reason } : {});

// ---------------------------------------------------------------------------
// Workflow (read-only derived view over PreOnboardingWorkflow + events)
// ---------------------------------------------------------------------------

/** Get the workflow attached to an application, if one has been bootstrapped. */
export const getApplicationWorkflow = (publicRef) =>
  api.get(`${BASE}/applications/${encodeURIComponent(publicRef)}/workflow/`);

/** Direct lookup by numeric workflow id (used for deep-links from events). */
export const getWorkflow = (workflowId) =>
  api.get(`${BASE}/workflows/${workflowId}/`);

/** Step-event timeline for an application's workflow. */
export const getApplicationEvents = (publicRef) =>
  api.get(`${BASE}/applications/${encodeURIComponent(publicRef)}/events/`);

// ---------------------------------------------------------------------------
// Phase 2: step mutations
// ---------------------------------------------------------------------------

/**
 * Helper that builds the `/applications/<ref>/steps/<key>/<action>/` URL.
 * We route through a single helper to keep the dispatch logic consistent
 * across components (and to make it easy to add optimistic-UI handling in
 * a single place if we need it later).
 */
const stepActionUrl = (publicRef, stepKey, action) =>
  `${BASE}/applications/${encodeURIComponent(publicRef)}/steps/${encodeURIComponent(stepKey)}/${action}/`;

/**
 * Mark a step as complete.
 * ``payload`` is optional — consumer-specific metadata (stored on the
 * resulting PreOnboardingStepEvent.payload).
 */
export const completeStep = (publicRef, stepKey, { reason = '', payload = null } = {}) =>
  api.post(stepActionUrl(publicRef, stepKey, 'complete'), {
    reason,
    ...(payload ? { payload } : {}),
  });

/** Skip a step with a mandatory reason. */
export const skipStep = (publicRef, stepKey, reason, payload = null) =>
  api.post(stepActionUrl(publicRef, stepKey, 'skip'), {
    reason,
    ...(payload ? { payload } : {}),
  });

/**
 * Block a step (usually pauses the whole application: the backend will
 * auto-transition CandidateApplication.status to 'blocked' if a blocking
 * step is put on hold).
 */
export const blockStep = (publicRef, stepKey, reason, payload = null) =>
  api.post(stepActionUrl(publicRef, stepKey, 'block'), {
    reason,
    ...(payload ? { payload } : {}),
  });

/**
 * Unblock a previously blocked step. Returns the application to 'in_progress'
 * (or directly to 'ready_to_hire' if everything else was already satisfied
 * while the block was in place).
 */
export const unblockStep = (publicRef, stepKey, { reason = '' } = {}) =>
  api.post(stepActionUrl(publicRef, stepKey, 'unblock'), { reason });

/**
 * HR-only: override a step (bypass its compliance check) with a recorded
 * reason. The resulting event keeps type='override' so auditors can filter.
 */
export const overrideStep = (publicRef, stepKey, reason, payload = null) =>
  api.post(stepActionUrl(publicRef, stepKey, 'override'), {
    reason,
    ...(payload ? { payload } : {}),
  });

// ---------------------------------------------------------------------------
// Phase 2: document uploads (offer letter + supporting docs)
// ---------------------------------------------------------------------------

/**
 * Document type constants — mirror
 * staff_confidential.pre_onboarding_models.CandidateDocument.DOCUMENT_TYPE_CHOICES.
 */
export const DOC_OFFER_LETTER = 'offer_letter';
export const DOC_SIGNED_OFFER = 'signed_offer_letter';
export const DOC_DECLARATION = 'declaration_form';
export const DOC_MEDICAL_CERT = 'medical_certificate';
export const DOC_PASSPORT = 'passport';
export const DOC_NRIC = 'nric';
export const DOC_WORK_PASS_FRONT = 'work_pass_front';
export const DOC_WORK_PASS_BACK = 'work_pass_back';
export const DOC_EDUCATION_CERT = 'education_certificate';
export const DOC_OTHER = 'other';

export const DOCUMENT_TYPE_LABELS = {
  [DOC_OFFER_LETTER]: 'Offer Letter (HR → Candidate)',
  [DOC_SIGNED_OFFER]: 'Signed Offer Letter (Candidate return)',
  [DOC_DECLARATION]: 'Declaration Form',
  [DOC_MEDICAL_CERT]: 'Medical Certificate',
  [DOC_PASSPORT]: 'Passport Scan',
  [DOC_NRIC]: 'NRIC / FIN Scan',
  [DOC_WORK_PASS_FRONT]: 'Work Pass Card (Front)',
  [DOC_WORK_PASS_BACK]: 'Work Pass Card (Back)',
  [DOC_EDUCATION_CERT]: 'Education Certificate',
  [DOC_OTHER]: 'Other Supporting Document',
};

/**
 * Upload a candidate document (multipart/form-data).
 *
 * Uploading a ``signed_offer_letter`` triggers the backend to auto-complete
 * the ``loo_accepted_by_candidate`` step. The response shape is:
 *
 *   {
 *     document: { ... CandidateDocument serialization ... },
 *     auto_completed_step?: { event, workflow, application_status, ... }
 *   }
 */
export const uploadDocument = (publicRef, documentType, file, { notes = '' } = {}) => {
  const form = new FormData();
  form.append('document_type', documentType);
  form.append('file', file);
  if (notes) form.append('notes', notes);
  return api.post(
    `${BASE}/applications/${encodeURIComponent(publicRef)}/documents/`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

/** List currently-active (non-superseded) documents for an application. */
export const listDocuments = (publicRef) =>
  api.get(`${BASE}/applications/${encodeURIComponent(publicRef)}/documents-list/`);

// ---------------------------------------------------------------------------
// Phase 2: work pass submission + IPA capture
// ---------------------------------------------------------------------------

/**
 * Record that the work pass application was lodged with MOM. Auto-completes
 * the ``wp_application_submitted`` step.
 */
export const captureWorkPassSubmission = (
  publicRef,
  { passType, submittedAt = null, rejectionReason = '' } = {},
) =>
  api.post(
    `${BASE}/applications/${encodeURIComponent(publicRef)}/work-pass-submission/`,
    {
      pass_type: passType,
      ...(submittedAt ? { submitted_at: submittedAt } : {}),
      ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
    },
  );

/**
 * Record IPA receipt with the uploaded IPA letter. Auto-completes the
 * ``ipa_received`` step.
 */
export const captureIPA = (publicRef, { passType, file, ipaIssuedAt = null } = {}) => {
  const form = new FormData();
  form.append('pass_type', passType);
  form.append('ipa_letter', file);
  if (ipaIssuedAt) form.append('ipa_issued_at', ipaIssuedAt);
  return api.post(
    `${BASE}/applications/${encodeURIComponent(publicRef)}/ipa-capture/`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

// ---------------------------------------------------------------------------
// Form B: Work Pass Application Form (HR review + send-to-candidate)
// ---------------------------------------------------------------------------

/**
 * Fetch the merged Work Pass Application Form (Form B) for HR review.
 * Response: { public_ref, status, prefilled_keys, sent_at, completed_at, values }.
 * Creates + prefills the draft on first access.
 */
export const getWorkPassForm = (publicRef) =>
  api.get(`${BASE}/applications/${encodeURIComponent(publicRef)}/work-pass-application-form/`);

/**
 * Email the candidate a magic link to complete Form B, mark it sent, and
 * auto-complete the ``work_pass_form_sent`` step. WP / SP / EP only.
 * Response: { form, portal_url, sent_to, auto_completed_step? }.
 */
export const sendWorkPassForm = (publicRef) =>
  api.post(`${BASE}/applications/${encodeURIComponent(publicRef)}/work-pass-application-form/send/`, {});

// Pass-type enum mirror.
export const PASS_WP = 'wp';
export const PASS_SP = 'sp';
export const PASS_EP = 'ep';
export const PASS_NA = 'na';

export const PASS_LABELS = {
  [PASS_WP]: 'Work Permit',
  [PASS_SP]: 'S Pass',
  [PASS_EP]: 'Employment Pass',
  [PASS_NA]: 'Not Applicable (Local / PR)',
};

// ---------------------------------------------------------------------------
// Dashboard-style aggregates (used by PreOnboardingTab)
// ---------------------------------------------------------------------------

/**
 * Client-side summariser — counts applications by status and track from a
 * list response. Kept here (not in the component) so both the tab and any
 * future widget can share the exact same shape.
 */
export const summariseApplications = (applications = []) => {
  const byStatus = {};
  const byTrack = {};
  for (const app of applications) {
    byStatus[app.status] = (byStatus[app.status] || 0) + 1;
    byTrack[app.track] = (byTrack[app.track] || 0) + 1;
  }
  return {
    total: applications.length,
    byStatus,
    byTrack,
  };
};

// ---------------------------------------------------------------------------
// Track / status / source enum mirrors (kept 1:1 with backend model choices)
// ---------------------------------------------------------------------------

export const TRACK_PENDING = 'pending_hr_review';
export const TRACK_WORK_PERMIT = 'work_permit';
export const TRACK_S_PASS_EP = 's_pass_ep';
export const TRACK_LOCAL_PR = 'local_pr';

export const TRACK_LABELS = {
  [TRACK_PENDING]: 'Pending HR Review',
  [TRACK_WORK_PERMIT]: 'Work Permit',
  [TRACK_S_PASS_EP]: 'S Pass / EP',
  [TRACK_LOCAL_PR]: 'Local / PR',
};

// HR-owned applicant classification. NO default — HR must consciously choose.
// intern/advisor remain valid values but their playbook is on hold (d3); only
// staff + worker are wired end-to-end this iteration.
export const CLASSIFICATION_WORKER = 'worker';
export const CLASSIFICATION_STAFF = 'staff';
export const CLASSIFICATION_INTERN = 'intern';
export const CLASSIFICATION_ADVISOR = 'advisor';

export const CLASSIFICATION_LABELS = {
  [CLASSIFICATION_WORKER]: 'Worker',
  [CLASSIFICATION_STAFF]: 'Staff',
  [CLASSIFICATION_INTERN]: 'Intern',
  [CLASSIFICATION_ADVISOR]: 'Advisor',
};

// Classifications HR can action right now (staff + worker). intern/advisor are
// deferred with their playbook (d3) and intentionally not selectable yet.
export const SELECTABLE_CLASSIFICATIONS = [
  CLASSIFICATION_WORKER,
  CLASSIFICATION_STAFF,
];

export const STATUS_DRAFT = 'draft';
export const STATUS_ACCEPTED = 'accepted';
export const STATUS_IN_PROGRESS = 'in_progress';
export const STATUS_BLOCKED = 'blocked';
export const STATUS_READY_TO_HIRE = 'ready_to_hire';
export const STATUS_HIRED = 'hired';
export const STATUS_WITHDRAWN = 'withdrawn';
export const STATUS_REJECTED = 'rejected';

export const STATUS_LABELS = {
  [STATUS_DRAFT]: 'Draft',
  [STATUS_ACCEPTED]: 'Offer Accepted',
  [STATUS_IN_PROGRESS]: 'In Progress',
  [STATUS_BLOCKED]: 'Blocked',
  [STATUS_READY_TO_HIRE]: 'Ready to Hire',
  [STATUS_HIRED]: 'Hired',
  [STATUS_WITHDRAWN]: 'Withdrawn',
  [STATUS_REJECTED]: 'Rejected',
};

export const SOURCE_ATS = 'ats';
export const SOURCE_HR_MANUAL = 'hr_manual';
export const SOURCE_CANDIDATE_PORTAL = 'candidate_portal';

export const SOURCE_LABELS = {
  [SOURCE_ATS]: 'ATS',
  [SOURCE_HR_MANUAL]: 'HR Manual',
  [SOURCE_CANDIDATE_PORTAL]: 'Candidate Portal',
};

// ---------------------------------------------------------------------------
// Default export — convenience object for consumers that prefer a namespace.
// ---------------------------------------------------------------------------

const preOnboardingAPI = {
  listApplications,
  getApplication,
  createApplication,
  directHire,
  updateApplication,
  withdrawApplication,
  transitionApplication,
  getApplicationWorkflow,
  getWorkflow,
  getApplicationEvents,
  summariseApplications,

  completeStep,
  skipStep,
  blockStep,
  unblockStep,
  overrideStep,

  uploadDocument,
  listDocuments,

  captureWorkPassSubmission,
  captureIPA,

  getWorkPassForm,
  sendWorkPassForm,
};

export default preOnboardingAPI;
